export type AdminTab = 'users' | 'history' | 'reviews' | 'tags' | 'inquiries';

export type AdminFilters = {
  sort: string;
  type: string;
  priceLevel: string;
  minRating: number;
  maxRating: number;
};

export const DEFAULT_ADMIN_FILTERS: Record<AdminTab, AdminFilters> = {
  users: {
    sort: 'name-asc',
    type: 'all',
    priceLevel: 'all',
    minRating: 0,
    maxRating: 5,
  },
  history: {
    sort: 'created-desc',
    type: 'all',
    priceLevel: 'all',
    minRating: 0,
    maxRating: 5,
  },
  reviews: {
    sort: 'created-desc',
    type: 'all',
    priceLevel: 'all',
    minRating: 0,
    maxRating: 5,
  },
  tags: {
    sort: 'name-asc',
    type: 'all',
    priceLevel: 'all',
    minRating: 0,
    maxRating: 5,
  },
  inquiries: {
    sort: 'created-desc',
    type: 'all',
    priceLevel: 'all',
    minRating: 0,
    maxRating: 5,
  },
};

const searchableText = (values: unknown[]) =>
  values.filter(Boolean).join(' ').toLocaleLowerCase();

const timestamp = (value: any) => {
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const parsed = value instanceof Date ? value.getTime() : Date.parse(value || '');
  return Number.isNaN(parsed) ? 0 : parsed;
};

const compareNames = (first: string, second: string, locale: string) =>
  first.localeCompare(second, locale, { sensitivity: 'base' });

export const hasActiveAdminFilters = (tab: AdminTab, filters: AdminFilters) => {
  const defaults = DEFAULT_ADMIN_FILTERS[tab];
  return Object.keys(defaults).some(
    (key) => filters[key as keyof AdminFilters] !== defaults[key as keyof AdminFilters],
  );
};

export const filterAdminItems = (
  tab: AdminTab,
  items: any[],
  search: string,
  filters: AdminFilters,
  locale: string,
) => {
  const query = search.trim().toLocaleLowerCase();

  return items
    .filter((item) => {
      if (tab === 'users') {
        const matchesSearch = searchableText([
          item.firstName,
          item.lastName,
          item.username,
          item.email,
        ]).includes(query);
        const matchesType = filters.type === 'all'
          || (filters.type === 'banned' && item.banned)
          || (filters.type === 'active' && !item.banned)
          || (filters.type === 'admin' && item.isAdmin);
        return matchesSearch && matchesType;
      }

      if (tab === 'history') {
        const rating = Number(item.rating) || 0;
        const matchesSearch = searchableText([
          item.name,
          item.address,
          item.createdByName,
        ]).includes(query);
        const matchesType = filters.type === 'all' || item.type === filters.type;
        const matchesPrice = filters.priceLevel === 'all'
          || Number(item.priceLevel) === Number(filters.priceLevel);
        return matchesSearch
          && matchesType
          && matchesPrice
          && rating >= filters.minRating
          && rating <= filters.maxRating;
      }

      if (tab === 'reviews') {
        const rating = Number(item.rating) || 0;
        const matchesSearch = searchableText([
          item.authorName,
          item.authorUsername,
          item.placeName,
          item.text,
        ]).includes(query);
        return matchesSearch
          && rating >= filters.minRating
          && rating <= filters.maxRating;
      }

      if (tab === 'tags') {
        return searchableText([item.name]).includes(query);
      }

      const matchesSearch = searchableText([
        item.subject,
        item.message,
        item.senderName,
        item.senderUsername,
        item.placeName,
      ]).includes(query);
      const hasAttachments = (item.attachments || []).length > 0;
      const matchesType = filters.type === 'all'
        || (filters.type === 'attachments' && hasAttachments)
        || (filters.type === 'no-attachments' && !hasAttachments);
      return matchesSearch && matchesType;
    })
    .sort((first, second) => {
      const firstName = tab === 'users'
        ? `${first.firstName || ''} ${first.lastName || ''}`.trim() || first.username || ''
        : tab === 'history' ? first.name || ''
          : tab === 'reviews' ? first.authorName || first.authorUsername || ''
            : tab === 'tags' ? first.name || ''
            : first.subject || '';
      const secondName = tab === 'users'
        ? `${second.firstName || ''} ${second.lastName || ''}`.trim() || second.username || ''
        : tab === 'history' ? second.name || ''
          : tab === 'reviews' ? second.authorName || second.authorUsername || ''
            : tab === 'tags' ? second.name || ''
            : second.subject || '';

      if (filters.sort === 'name-asc') return compareNames(firstName, secondName, locale);
      if (filters.sort === 'name-desc') return compareNames(secondName, firstName, locale);
      if (filters.sort === 'rating-desc') return (Number(second.rating) || 0) - (Number(first.rating) || 0);
      if (filters.sort === 'rating-asc') return (Number(first.rating) || 0) - (Number(second.rating) || 0);

      const dateKey = tab === 'users' && filters.sort.startsWith('login-')
        ? 'lastLogin'
        : 'createdAt';
      const difference = timestamp(first[dateKey]) - timestamp(second[dateKey]);
      return filters.sort.endsWith('-asc') ? difference : -difference;
    });
};
